/**
 * LeadCopyPanel — assistente conversacional embutido no editor de e-mail
 * e no FlowBuilder.
 *
 * Modos:
 *  - "email"    : conversa sobre a copy do e-mail aberto.
 *  - "flow"     : conversa sobre o fluxo de automação (próximos nodes, copy).
 *  - "campaign" : conversa sobre estrutura de campanha (lançamento, série).
 *
 * Renderiza o AIChatBox e parseia blocos ```action``` no markdown como
 * cards "Aplicar" via `onApplyAction`.
 */

import { useState, useMemo } from "react";
import { Sparkles, Wand2, Paperclip, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Streamdown } from "streamdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send, User, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { ThinkingIndicator } from "./ThinkingIndicator";

// ── Tipos espelham server/ai-email/leadCopyChat.ts ──────────────────────────

export type ChatRole = "user" | "assistant";

export type ChatMessage = { role: ChatRole; content: string };

export type CopyStyle = "lightcopy" | "agencia" | "launch" | "default";

export type ChatMode = "email" | "flow" | "campaign";

export type ContextRef =
  | { kind: "email"; id: number; subject?: string; preheader?: string; bodySnippet?: string }
  | { kind: "flow"; id: number; name?: string; nodeCount?: number; nodesSummary?: string }
  | { kind: "campaign"; id: number; name?: string; nodeCount?: number; nodesSummary?: string };

export type ChatAction =
  | { kind: "applyToEmail"; field: "subject" | "preheader" | "body"; value: string }
  | { kind: "applyToNode"; nodeId: string; field: string; value: string }
  | { kind: "suggestNode"; nodeType: string; label: string; config: Record<string, unknown> };

export type LeadCopyPanelProps = {
  mode: ChatMode;
  contextRef?: ContextRef;
  onApplyAction?: (action: ChatAction) => void;
  productId?: number;
  audienceId?: number;
  /** Sugestões iniciais clicáveis no estado vazio */
  suggestedPrompts?: string[];
  className?: string;
};

const STYLE_OPTIONS: { value: CopyStyle; label: string }[] = [
  { value: "lightcopy", label: "LightCopy (sincero)" },
  { value: "agencia",   label: "Agência de Copy" },
  { value: "launch",    label: "Lançamento (Jeff Walker)" },
  { value: "default",   label: "Padrão (clareza direta)" },
];

// Sugestões variam com o estado do recurso (ex: subject vazio vs preenchido).
function getDefaultPrompts(mode: ChatMode, ref?: ContextRef): string[] {
  if (mode === "email") {
    const hasSubject = ref?.kind === "email" && !!ref.subject?.trim();
    const hasBody = ref?.kind === "email" && !!ref.bodySnippet?.trim();
    return hasSubject
      ? [
          "Reescreva esse assunto em 3 variações mais curtas.",
          hasBody ? "O corpo está alinhado ao assunto? Onde aparar?" : "Escreva uma abertura que use observação cotidiana.",
          "Crie um preheader que complemente o assunto sem repetir.",
          "Sugira 2 P.S. (post-scriptum) para esse e-mail.",
        ]
      : [
          "Sugira 3 assuntos curtos e sinceros pra esse e-mail.",
          "Escreva uma abertura honesta usando observação cotidiana.",
          "Estrutura: Premissa → Premissa → Conclusão. Aplica aqui.",
        ];
  }
  if (mode === "flow") {
    return [
      "Qual o próximo node lógico depois desse?",
      "Reescreva a copy do e-mail 1 usando observação cotidiana.",
      "Esse fluxo está balanceado em ritmo? Sugira ajustes.",
      "Onde adicionar uma condição pra segmentar quem abriu vs quem não abriu?",
    ];
  }
  // campaign
  return [
    "Monte a estrutura de PLCs de pré-lançamento pra esse produto.",
    "Sugira 4 e-mails pra fase de carrinho aberto.",
    "Que gatilhos mentais cabem aqui sem soar manipulador?",
    "Como fazer o e-mail pós-lançamento pra quem não comprou (sem culpa)?",
  ];
}

// Regex idêntico ao do server (extrai blocos ```action ... ```).
// matchAll é stateless — não compartilha lastIndex entre chamadas.
const ACTION_BLOCK_PATTERN = /```action\s*\n([\s\S]*?)\n```/g;

function parseActionsFromMarkdown(markdown: string): ChatAction[] {
  const actions: ChatAction[] = [];
  for (const match of markdown.matchAll(ACTION_BLOCK_PATTERN)) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed && typeof parsed === "object" && typeof parsed.kind === "string") {
        actions.push(parsed as ChatAction);
      }
    } catch {
      // ignora bloco malformado
    }
  }
  return actions;
}

function stripActionBlocks(markdown: string): string {
  // String#replace não compartilha lastIndex (cria novo regex internamente).
  return markdown.replace(ACTION_BLOCK_PATTERN, "").trim();
}

// ── Card de ação aplicável ─────────────────────────────────────────────────

function ActionCard({ action, onApply, applied }: { action: ChatAction; onApply: () => void; applied: boolean }) {
  let title = "Aplicar sugestão";
  let preview = "";
  if (action.kind === "applyToEmail") {
    title = action.field === "subject" ? "Aplicar este assunto"
          : action.field === "preheader" ? "Aplicar este preheader"
          : "Substituir corpo do e-mail";
    preview = action.value;
  } else if (action.kind === "applyToNode") {
    title = `Aplicar no node (${action.field})`;
    preview = action.value;
  } else if (action.kind === "suggestNode") {
    title = `Adicionar node: ${action.label}`;
    preview = `Tipo: ${action.nodeType}`;
  }

  return (
    <Card className={cn(
      "my-2 p-3 transition-colors",
      applied ? "bg-emerald-50 border-emerald-200 opacity-70" : "bg-primary/5 border-primary/30",
    )}>
      <div className="flex items-start gap-2">
        <Wand2 className={cn("size-3.5 mt-0.5 shrink-0", applied ? "text-emerald-600" : "text-primary")} />
        <div className="flex-1 min-w-0">
          <div className={cn("text-xs font-medium", applied ? "text-emerald-700" : "text-primary")}>
            {applied ? `✓ ${title.replace(/^Aplicar /, "Aplicado: ").replace(/^Substituir /, "Aplicado: ").replace(/^Adicionar /, "Adicionado: ")}` : title}
          </div>
          {preview && (
            <div className="text-xs text-muted-foreground mt-1 line-clamp-3 whitespace-pre-wrap">
              {preview}
            </div>
          )}
        </div>
        {applied ? (
          <span className="text-[10px] text-emerald-700 font-medium px-2 shrink-0 self-center">Aplicado</span>
        ) : (
          <Button size="sm" variant="default" className="h-6 text-[11px] px-2 shrink-0" onClick={onApply}>
            Aplicar
          </Button>
        )}
      </div>
    </Card>
  );
}

// ── Componente principal ───────────────────────────────────────────────────

export function LeadCopyPanel({
  mode,
  contextRef,
  onApplyAction,
  productId,
  audienceId,
  suggestedPrompts,
  className,
}: LeadCopyPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [style, setStyle] = useState<CopyStyle>("lightcopy");
  const [input, setInput] = useState("");
  const [attachedDocIds, setAttachedDocIds] = useState<number[]>([]);
  const [planLocked, setPlanLocked] = useState(false);
  // Marca quais actions já foram aplicadas. Chave: `${msgIndex}:${actionIndex}`
  const [appliedActions, setAppliedActions] = useState<Set<string>>(new Set());

  // Lista documentos da Inteligência (silenciosa em caso de FORBIDDEN)
  const { data: docs } = trpc.knowledgeBase.list.useQuery(undefined, {
    retry: false,
    enabled: true,
  });
  const docList = (docs as Array<{ id: number; title: string; status: string }> | undefined) ?? [];

  const chatMutation = trpc.aiEmail.chat.useMutation({
    onSuccess: (response: { message: string; actions: ChatAction[] }) => {
      // Garante content non-empty (Zod do servidor exige >=1 char no próximo turn)
      const content = (response.message ?? "").trim();
      if (!content && (!response.actions || response.actions.length === 0)) {
        // Marcador especial: o frontend renderiza um card de erro com botão "Tentar novamente"
        setMessages((prev) => [...prev, { role: "assistant", content: "__EMPTY_RESPONSE__" }]);
        return;
      }
      const finalContent = content || "_(sugestões aplicáveis acima)_";
      setMessages((prev) => [...prev, { role: "assistant", content: finalContent }]);
    },
    onError: (err) => {
      const e = err as unknown as { message: string; data?: { code?: string } };
      const isFeatureGate =
        e.data?.code === "FORBIDDEN" || /Recurso não habilitado|ai_email_assistant/i.test(e.message);
      if (isFeatureGate) {
        setPlanLocked(true);
        return;
      }
      toast.error(toUserFriendlyError(e, "Erro do LeadCopy"));
    },
  });

  const isLoading = chatMutation.isPending;

  const send = (content: string) => {
    const trimmed = content.trim();
    if (!trimmed || isLoading) return;
    // Sanitiza histórico: filtra mensagens vazias e marcadores internos (Zod exige min(1))
    const cleanHistory = messages.filter(
      (m) => m.content && m.content.trim().length > 0 && m.content !== "__EMPTY_RESPONSE__",
    );
    const next: ChatMessage[] = [...cleanHistory, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    chatMutation.mutate({
      messages: next,
      mode,
      style,
      contextRef,
      productId,
      audienceId,
      documentIds: attachedDocIds.length > 0 ? attachedDocIds : undefined,
    } as any);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  const prompts = useMemo(() => suggestedPrompts ?? getDefaultPrompts(mode, contextRef), [mode, contextRef, suggestedPrompts]);

  const toggleDoc = (id: number) => {
    setAttachedDocIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const retryLast = () => {
    // Remove a placeholder vazia E o último user message; reenvia esse user message.
    const cleaned = messages.filter((m) => m.content !== "__EMPTY_RESPONSE__");
    const lastUser = [...cleaned].reverse().find((m) => m.role === "user");
    if (!lastUser) return;
    const beforeLastUser = cleaned.slice(0, cleaned.lastIndexOf(lastUser));
    setMessages(beforeLastUser);
    setTimeout(() => send(lastUser.content), 0);
  };

  return (
    <div className={cn("flex flex-col h-full bg-background overflow-hidden", className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-muted/30 shrink-0">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="size-4 text-primary" />
          LeadCopy
        </div>
        <Select value={style} onValueChange={(v) => setStyle(v as CopyStyle)}>
          <SelectTrigger className="h-7 text-xs w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STYLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Plano Free: bloqueio amigável */}
      {planLocked ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="size-14 rounded-full bg-amber-100 flex items-center justify-center">
            <Lock className="size-7 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-1">LeadCopy é exclusivo do plano Pró</h3>
            <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
              O assistente conversacional, geração contextual e materialização de e-mails só ficam ativos no plano Pró
              ou superior. Faça upgrade para liberar.
            </p>
          </div>
          <Button asChild size="sm" className="gap-1.5">
            <a href="/settings?tab=plan">
              <Sparkles className="size-3.5" />
              Ver planos
            </a>
          </Button>
          <button
            type="button"
            onClick={() => setPlanLocked(false)}
            className="text-[11px] text-muted-foreground hover:text-foreground underline"
          >
            Tentar novamente
          </button>
        </div>
      ) : null}

      {/* Messages */}
      {!planLocked && (
      <div className="flex-1 overflow-hidden">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 p-4 text-muted-foreground">
            {isLoading ? (
              <ThinkingIndicator mode={mode} />
            ) : (
              <>
                <Sparkles className="size-10 opacity-20" />
                <p className="text-sm text-center">
                  Pergunte algo sobre o {mode === "email" ? "e-mail" : mode === "campaign" ? "lançamento" : "fluxo"} aberto.
                </p>
                <div className="flex flex-col w-full gap-2">
                  {prompts.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      disabled={isLoading}
                      className="rounded-lg border border-border bg-card px-3 py-2 text-xs text-left transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="flex flex-col space-y-4 p-3">
              {messages.map((message, index) => {
                const isEmptyError = message.role === "assistant" && message.content === "__EMPTY_RESPONSE__";
                const actions = !isEmptyError && message.role === "assistant" ? parseActionsFromMarkdown(message.content) : [];
                const visibleContent = message.role === "assistant" ? stripActionBlocks(message.content) : message.content;

                if (isEmptyError) {
                  return (
                    <div key={index} className="flex items-start gap-2">
                      <div className="size-7 shrink-0 mt-0.5 rounded-full bg-amber-100 flex items-center justify-center">
                        <Sparkles className="size-3.5 text-amber-600" />
                      </div>
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs flex-1">
                        <p className="text-amber-800 mb-1.5">A IA não devolveu uma resposta utilizável dessa vez.</p>
                        <p className="text-amber-700/80 mb-2">Pode ter sido contexto insuficiente ou o modelo travou. Tente reformular ou repetir.</p>
                        <Button size="sm" variant="outline" className="h-6 text-[11px] gap-1" onClick={retryLast}>
                          Tentar novamente
                        </Button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={index}>
                    <div className={cn("flex gap-2", message.role === "user" ? "justify-end items-start" : "justify-start items-start")}>
                      {message.role === "assistant" && (
                        <div className="size-7 shrink-0 mt-0.5 rounded-full bg-primary/10 flex items-center justify-center">
                          <Sparkles className="size-3.5 text-primary" />
                        </div>
                      )}
                      <div className={cn(
                        "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                        message.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                      )}>
                        {message.role === "assistant" ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
                            <Streamdown>{visibleContent}</Streamdown>
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        )}
                      </div>
                      {message.role === "user" && (
                        <div className="size-7 shrink-0 mt-0.5 rounded-full bg-secondary flex items-center justify-center">
                          <User className="size-3.5 text-secondary-foreground" />
                        </div>
                      )}
                    </div>
                    {actions.length > 0 && (
                      <div className="ml-9 mt-1">
                        {actions.length >= 2 && (() => {
                          const allApplied = actions.every((_, ai) => appliedActions.has(`${index}:${ai}`));
                          return allApplied ? (
                            <div className="w-full mb-2 h-7 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center justify-center gap-1.5 font-medium">
                              <Wand2 className="size-3.5" />
                              Tudo aplicado no editor
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="default"
                              className="w-full mb-2 h-7 text-xs gap-1.5"
                              onClick={() => {
                                if (!onApplyAction) {
                                  toast.info("Editor não conectado.");
                                  return;
                                }
                                let count = 0;
                                actions.forEach((a, ai) => {
                                  const key = `${index}:${ai}`;
                                  if (!appliedActions.has(key)) {
                                    onApplyAction(a);
                                    count++;
                                  }
                                });
                                setAppliedActions((prev) => {
                                  const next = new Set(prev);
                                  actions.forEach((_, ai) => next.add(`${index}:${ai}`));
                                  return next;
                                });
                                if (count > 0) toast.success(`${count} aplicação${count === 1 ? "" : "ões"} no editor.`);
                              }}
                            >
                              <Wand2 className="size-3.5" />
                              Aplicar tudo no editor
                            </Button>
                          );
                        })()}
                        {actions.map((a, ai) => {
                          const key = `${index}:${ai}`;
                          const isApplied = appliedActions.has(key);
                          return (
                            <ActionCard
                              key={ai}
                              action={a}
                              applied={isApplied}
                              onApply={() => {
                                if (!onApplyAction) {
                                  toast.info("Ação recebida, mas o editor não tem manipulador conectado.");
                                  return;
                                }
                                onApplyAction(a);
                                setAppliedActions((prev) => new Set(prev).add(key));
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {isLoading && <ThinkingIndicator mode={mode} />}
            </div>
          </ScrollArea>
        )}
      </div>
      )}

      {/* Documentos anexados (chips) */}
      {attachedDocIds.length > 0 && (
        <div className="flex flex-wrap gap-1 px-3 py-1.5 border-t bg-muted/20 shrink-0">
          {attachedDocIds.map((id) => {
            const doc = docList.find((d) => d.id === id);
            if (!doc) return null;
            return (
              <Badge key={id} variant="secondary" className="text-[10px] gap-1 max-w-[180px]">
                <Paperclip className="size-2.5" />
                <span className="truncate">{doc.title}</span>
                <button onClick={() => toggleDoc(id)} className="ml-0.5 hover:text-destructive">×</button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-2 p-3 border-t bg-background/50 items-end shrink-0">
        {docList.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="shrink-0 h-9 w-9" title="Anexar documento da Inteligência">
                <Paperclip className="size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[calc(100vw-2rem)] sm:w-64 p-2">
              <div className="text-xs font-semibold mb-1.5">Documentos da Inteligência</div>
              <ScrollArea className="h-48">
                {docList.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 px-1 py-1 text-xs cursor-pointer hover:bg-muted rounded">
                    <Checkbox
                      checked={attachedDocIds.includes(d.id)}
                      onCheckedChange={() => toggleDoc(d.id)}
                      disabled={d.status !== "ready"}
                    />
                    <span className="flex-1 truncate">{d.title}</span>
                    {d.status !== "ready" && (
                      <span className="text-[9px] text-muted-foreground">{d.status}</span>
                    )}
                  </label>
                ))}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          placeholder="Pergunte ao LeadCopy…"
          className="flex-1 max-h-32 resize-none min-h-9 text-sm"
          rows={1}
        />
        <Button
          type="submit"
          size="icon"
          disabled={!input.trim() || isLoading}
          className="shrink-0 h-9 w-9"
          aria-label="Enviar"
        >
          {isLoading ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </Button>
      </form>
    </div>
  );
}
